import * as React from "react"

import * as TabsPrimitives from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

/* ── CVA variants ─────────────────────────────────────────── */

const tabsListVariants = cva(["inline-flex items-center"], {
  variants: {
    variant: {
      underline: "gap-1 border-b border-coval-border",
      // Control height is a floor, not a cap, so a label that wraps grows the
      // bar instead of spilling out of it.
      pill: "gap-0.5 min-h-control rounded-md bg-surface-contrast border border-surface-1 p-[2px]",
    },
  },
  defaultVariants: {
    variant: "underline",
  },
})

const tabsTriggerVariants = cva(
  [
    // Wrapping lets a label that cannot share a line with its icon drop below
    // it, so a cramped trigger stacks icon over text instead of overflowing.
    "inline-flex min-w-0 flex-wrap cursor-pointer gap-1 px-3 py-2",
    "items-center content-center justify-center",
    "text-center text-sm font-medium break-words",
    "[&_svg]:shrink-0",
    "text-coval-text-muted",
    "transition-colors duration-150",
    "not-disabled:hover:text-coval-text",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active focus-visible:ring-offset-1",
    "disabled:cursor-default disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        underline: [
          "data-[state=active]:text-coval-text",
          "data-[state=active]:shadow-[inset_0_-2px_0_0_var(--coval-contrast)]",
        ],
        pill: [
          // Padding keeps a wrapped label off the pill edge. It is paired with
          // the list inset and line height so a single-line bar still measures
          // exactly one control tall.
          "self-stretch rounded-sm py-0.5 leading-4",
          // Border is reserved on every pill so activating one does not
          // widen it and shift its siblings.
          "border border-transparent",
          "data-[state=active]:bg-coval-surface-raised data-[state=active]:text-coval-text",
          "data-[state=active]:border-surface-1",
        ],
      },
    },
    defaultVariants: {
      variant: "underline",
    },
  },
)

const tabsContentVariants = cva(["mt-3", "focus-visible:outline-none"])

/* ── Tabs (root) ──────────────────────────────────────────── */

const Tabs = TabsPrimitives.Root

/* ── TabsList ─────────────────────────────────────────────── */

export interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitives.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitives.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitives.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
))

TabsList.displayName = "TabsList"

/* ── TabsTrigger ──────────────────────────────────────────── */

export interface TabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitives.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitives.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitives.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
))

TabsTrigger.displayName = "TabsTrigger"

/* ── TabsContent ──────────────────────────────────────────── */

export interface TabsContentProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitives.Content>,
    VariantProps<typeof tabsContentVariants> {}

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitives.Content>,
  TabsContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitives.Content
    ref={ref}
    className={cn(tabsContentVariants(), className)}
    {...props}
  />
))

TabsContent.displayName = "TabsContent"

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  tabsTriggerVariants,
  tabsContentVariants,
}
